import { useState } from "react";
import { Box, Chip, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { Edit, Save, Close } from "@mui/icons-material";
import { useUpdateActionItem } from "@/api/hooks/extraction";
import {
  ACTION_ITEM_STATUSES,
  ACTION_ITEM_STATUS_LABELS,
} from "@/api/labels";
import type { ActionItem, ActionItemStatus } from "@/api/types";

const STATUS_COLOR: Record<
  ActionItemStatus,
  "default" | "info" | "success" | "warning"
> = {
  open: "info",
  in_progress: "warning",
  done: "success",
  cancelled: "default",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ActionItemRow({
  item,
  transcriptId,
}: {
  item: ActionItem;
  transcriptId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<ActionItemStatus>(item.status);
  const [owner, setOwner] = useState(item.owner ?? "");
  const [dueDate, setDueDate] = useState(item.due_date ?? "");
  const updateMutation = useUpdateActionItem(transcriptId);

  const save = async () => {
    await updateMutation.mutateAsync({
      itemId: item.id,
      payload: {
        status,
        owner: owner.trim() || null,
        due_date: dueDate || null,
      },
    });
    setEditing(false);
  };

  const cancel = () => {
    setStatus(item.status);
    setOwner(item.owner ?? "");
    setDueDate(item.due_date ?? "");
    setEditing(false);
  };

  if (!editing) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Chip
          size="small"
          label={ACTION_ITEM_STATUS_LABELS[item.status]}
          color={STATUS_COLOR[item.status]}
        />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 500 }}>{item.description}</Typography>
          <Typography variant="caption" color="text.secondary">
            {item.owner ? `Owner: ${item.owner}` : "Owner: unassigned"}
            {" · "}Due: {formatDate(item.due_date)}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          aria-label={`Edit action item`}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 1,
      }}
    >
      <TextField
        size="small"
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as ActionItemStatus)}
        sx={{ width: 160 }}
      >
        {ACTION_ITEM_STATUSES.map((s) => (
          <MenuItem key={s} value={s}>
            {ACTION_ITEM_STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Owner"
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="date"
        label="Due date"
        slotProps={{ inputLabel: { shrink: true } }}
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
      />
      <IconButton
        size="small"
        onClick={save}
        disabled={updateMutation.isPending}
        aria-label="Save"
      >
        <Save fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={cancel}
        disabled={updateMutation.isPending}
        aria-label="Cancel"
      >
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
}
